import { useState, useCallback } from "react";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

// v3 marketing set (founder-uploaded, bleed-cropped, uniform 720x1200).
// Cards 02 and 09 stay out until fixed versions arrive (third-party team
// name / AI text glitches — nothing unpolished where App Review reads).
// Duplicated in the DOM so the CSS marquee loops seamlessly.
const SHOTS = [
  { src: "/generated/v3-01-hero.webp", alt: "GateMate welcome — create account or sign in, with concerts, sports, festivals and airports", caption: "Step In. Link Up." },
  { src: "/generated/v3-02-find-room.webp", alt: "Live Now — rooms you can step into right now: Rooftop Friday in Dallas, Game Day, DFW Airport, Live Concert in Austin, Lounge Night in Houston", caption: "Find your room." },
  { src: "/generated/v3-03-whats-happening.webp", alt: "Live GateRoom — Rooftop Friday with 124 here, GateMates present and room chat", caption: "See what's happening." },
  { src: "/generated/v3-04-join-code.webp", alt: "Open a Room — scan the poster QR or type the room code, no account needed", caption: "Join with a code." },
  { src: "/generated/v3-05-step-in.webp", alt: "Step In — pick an emoji or a photo, add your name and email", caption: "Step in as yourself." },
  { src: "/generated/v3-06-meet-whos-there.webp", alt: "Member profile — phone-verified mark, connections and hosted rooms", caption: "Meet who's there." },
  { src: "/generated/v3-07-airports.webp", alt: "Airports — Dallas Fort Worth, Dallas Love Field, Seattle, Houston, San Antonio, Austin and El Paso, each with city and state", caption: "7 airports. More connections." },
  { src: "/generated/v3-08-host-room.webp", alt: "New GateRoom — title, entry code, where, vibe and room type", caption: "Host your own room." },
  { src: "/generated/v3-09-share-invite.webp", alt: "Share GateRoom — post the ROOF24 invite to your story in Bold, Minimal or Photo style, QR code included", caption: "Share the invite." },
  { src: "/generated/v3-10-keep-private.webp", alt: "Open a Private Room — code only, never on the map, closes itself after 4 hours", caption: "Keep it private." },
];

const Frame = ({ shot, onOpen, idx, duplicate }) => (
  <button
    type="button"
    onClick={() => onOpen(idx)}
    aria-label={duplicate ? undefined : `Open ${shot.alt} in full view`}
    aria-hidden={duplicate || undefined}
    tabIndex={duplicate ? -1 : undefined}
    data-testid={`marquee-frame-${idx}`}
    className="relative shrink-0 mx-4 md:mx-6 w-[200px] md:w-[232px] cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan rounded-[2rem]"
  >
    <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/10 shadow-[0_20px_50px_rgba(0,198,174,0.18)] transition-transform group-hover:scale-[1.03]">
      <img
        src={shot.src.replace(".webp", "-sm.webp")}
        srcSet={`${shot.src.replace(".webp", "-sm.webp")} 480w, ${shot.src} 720w`}
        sizes="(max-width: 768px) 200px, 232px"
        alt={duplicate ? "" : shot.alt}
        loading="lazy"
        className="w-full h-auto block"
        width="720"
        height="1200"
      />
    </div>
    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-[10px] w-[70%] rounded-full bg-neon-cyan/25 blur-md" />
  </button>
);

export default function BetaScreensMarquee() {
  const [openIdx, setOpenIdx] = useState(-1);
  const isOpen = openIdx >= 0;
  const shot = isOpen ? SHOTS[openIdx % SHOTS.length] : null;

  const handleOpen = useCallback((i) => setOpenIdx(i % SHOTS.length), []);
  const close = useCallback(() => setOpenIdx(-1), []);
  const prev = useCallback(
    () => setOpenIdx((i) => (i <= 0 ? SHOTS.length - 1 : i - 1)),
    []
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i + 1) % SHOTS.length),
    []
  );

  const track = [...SHOTS, ...SHOTS]; // duplicate for seamless loop

  return (
    <section
      id="beta-screens"
      className="relative py-20 md:py-24 overflow-hidden"
      data-testid="beta-screens-marquee"
    >
      <div className="glow-orb w-[420px] h-[420px] bg-neon-cyan/10 top-1/2 -translate-y-1/2 -left-40" />
      <div className="glow-orb w-[420px] h-[420px] bg-neon-violet/10 top-1/2 -translate-y-1/2 -right-40" />

      <div className="container-x relative z-10">
        <Reveal className="max-w-2xl">
          <p className="overline mb-4">Inside the app</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[0.95]">
            Sample screens from <span className="text-gradient">the app</span>.
          </h2>
          <p className="mt-5 text-lg text-zinc-400 leading-relaxed max-w-xl">
            A look at the build members are using tonight. Tap any card to view it full-size.
          </p>
        </Reveal>
      </div>

      {/* Marquee track — full-bleed, gradient-masked at the edges, pauses on hover */}
      <div
        className="relative mt-10 md:mt-14 overflow-hidden group/marquee"
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
          maskImage:
            "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
        }}
      >
        <div
          data-testid="marquee-track"
          className="flex w-max animate-marquee-x will-change-transform group-hover/marquee:[animation-play-state:paused]"
        >
          {track.map((s, i) => (
            <Frame key={`${s.src}-${i}`} shot={s} onOpen={handleOpen} idx={i} duplicate={i >= SHOTS.length} />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={isOpen} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent
          data-testid="marquee-lightbox"
          className="max-w-[420px] w-[92vw] bg-transparent border-0 shadow-none p-0 [&>button]:hidden"
        >
          {/* Radix a11y — a title is required for screen readers. Hide visually. */}
          <VisuallyHidden asChild>
            <DialogTitle>{shot ? shot.alt : "GateMate app screenshot"}</DialogTitle>
          </VisuallyHidden>
          <VisuallyHidden asChild>
            <DialogDescription>
              {shot ? shot.caption : "Preview of a screen from the GateMate app."}
            </DialogDescription>
          </VisuallyHidden>
          {shot && (
            <div className="relative flex flex-col items-center">
              <div className="relative">
                <div className="rounded-[2rem] overflow-hidden ring-1 ring-white/15 shadow-[0_40px_80px_rgba(0,198,174,0.35)]">
                  <img
                    src={shot.src}
                    alt={shot.alt}
                    className="w-full h-auto block"
                    data-testid="lightbox-img"
                  />
                </div>
              </div>

              {/* caption */}
              <p className="mt-5 text-center text-sm text-zinc-300 max-w-sm px-4" data-testid="lightbox-caption">
                {shot.caption}
              </p>

              {/* controls */}
              <button
                type="button"
                onClick={close}
                aria-label="Close screenshot"
                data-testid="lightbox-close"
                className="absolute -top-2 -right-2 w-9 h-9 grid place-items-center rounded-full bg-black/80 ring-1 ring-white/15 text-white hover:bg-neon-cyan hover:text-black transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous screen"
                data-testid="lightbox-prev"
                className="absolute top-1/2 -translate-y-1/2 -left-4 md:-left-14 w-11 h-11 grid place-items-center rounded-full bg-black/80 ring-1 ring-white/15 text-white hover:bg-neon-cyan hover:text-black transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next screen"
                data-testid="lightbox-next"
                className="absolute top-1/2 -translate-y-1/2 -right-4 md:-right-14 w-11 h-11 grid place-items-center rounded-full bg-black/80 ring-1 ring-white/15 text-white hover:bg-neon-cyan hover:text-black transition-colors"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
